import { persist, createJSONStorage } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';

/**
 * Session truth lives HERE, decoded once per token write/rehydrate:
 *   kind 'siwe' — subject is a 0x wallet address (SIWE login; /user/login applies)
 *   kind 'did'  — subject is a did:… identity (DID OIDC login; no wallet bootstrap)
 *   kind null   — no token, or a token we cannot decode (fail-CLOSED: grants nothing)
 * Consumers must gate POSITIVELY on the kind they need instead of excluding
 * known-bad kinds — negative gates were the root of the /user/login 400 loop.
 */
export type SessionKind = 'siwe' | 'did' | null;

interface AuthTokenState {
  token: string | null;
  kind: SessionKind;
  subject: string | null;
  /** Backend uid, minted into the JWT's jti claim by both login doors. */
  uid: string | null;
  setToken: (token: string | null) => void;
  clearToken: () => void;
}

const decodeSession = (token: string | null): { kind: SessionKind; subject: string | null; uid: string | null } => {
  if (!token) return { kind: null, subject: null, uid: null };
  try {
    const b64 = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    // atob needs padding to a multiple of 4 — JWTs strip it.
    const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
    const sub = typeof payload.sub === 'string' ? payload.sub : '';
    const uid = typeof payload.jti === 'string' && payload.jti ? payload.jti : null;
    if (sub.startsWith('did:')) return { kind: 'did', subject: sub, uid };
    if (sub.startsWith('0x')) return { kind: 'siwe', subject: sub, uid };
    return { kind: null, subject: sub || null, uid: null };
  } catch {
    return { kind: null, subject: null, uid: null };
  }
};

export const authTokenStore = createStore<AuthTokenState>()(
  persist(
    set => ({
      token: null,
      kind: null,
      subject: null,
      uid: null,
      setToken: token => set({ token, ...decodeSession(token) }),
      clearToken: () => set({ token: null, kind: null, subject: null, uid: null }),
    }),
    {
      name: 'auth-token',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({ token: state.token }),
      // Only the token persists; kind/subject are re-derived on rehydrate so a
      // stored token can never disagree with its decoded kind.
      merge: (persisted, current) => {
        const token = (persisted as { token?: string | null } | undefined)?.token ?? null;
        return { ...current, token, ...decodeSession(token) };
      },
    }
  )
);

export const getAuthToken = () => authTokenStore.getState().token;
export const getSessionKind = (): SessionKind => authTokenStore.getState().kind;
export const getSessionSubject = (): string | null => authTokenStore.getState().subject;
export const setAuthToken = (token: string | null) => authTokenStore.getState().setToken(token);
export const clearAuthToken = () => authTokenStore.getState().clearToken();

/** Reactive view of the session (token + kind + subject). */
export const useSession = () => useStore(authTokenStore);
